import { isArray, isObject, isString } from 'lodash'

import type { AssistantMessage as PersistedAssistantMessage } from '~/data/api/client'
import { attachmentsFromPersistedMessage } from './attachments'
import type { AssistantMessage, AssistantToolCall } from './streamTypes'
import { billingDraftsFromTrace, parseToolTrace as parseTraceEntries } from './toolTrace'

/**
 * `action_outcome` messages are synthetic prompts the backend injects (as the "user") after an approved action runs,
 * so the model can confirm/diagnose it. They are internal plumbing, not operator-authored text, so we hide them when
 * replaying a conversation in the panel.
 */
const HIDDEN_MESSAGE_TYPES = new Set(['action_outcome'])

/** Maps persisted conversation messages into the in-memory chat model the panel renders. */
export default function mapPersistedMessages(messages: PersistedAssistantMessage[]): AssistantMessage[] {
  return messages
    .filter((message) => !HIDDEN_MESSAGE_TYPES.has(message.messageType ?? ''))
    .map(toAssistantMessage)
}

function toAssistantMessage(message: PersistedAssistantMessage): AssistantMessage {
  const id = message.id ?? crypto.randomUUID()
  const billingDrafts = billingDraftsFromTrace(parseTraceEntries(message.toolTrace), id)
  return {
    id,
    role: message.role === 'assistant' ? 'assistant' : 'user',
    content: message.content ?? '',
    thinking: message.thinkingTrace ?? message.thinkingPreview ?? '',
    toolCalls: parseToolTrace(message.toolTrace),
    attachments: attachmentsFromPersistedMessage(message),
    billingDrafts: billingDrafts.length > 0 ? billingDrafts : undefined
  }
}

/** Rebuilds the tool-call chips from the compact JSON trace persisted on an assistant message. */
function parseToolTrace(toolTrace: string | undefined): AssistantToolCall[] | undefined {
  if (!toolTrace) {
    return undefined
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(toolTrace)
  } catch {
    return undefined
  }

  if (!isArray(parsed)) {
    return undefined
  }

  const calls: AssistantToolCall[] = []
  parsed.forEach((entry, index) => {
    if (!isObject(entry)) {
      return
    }
    const record = entry as Record<string, unknown>
    const name = isString(record.tool) ? record.tool : 'tool'
    const summary = isString(record.result) ? record.result : undefined
    calls.push({ id: `${index}-${name}`, name, summary, status: 'done' })
  })

  return calls.length > 0 ? calls : undefined
}
